/* Solstice Shopify — dedicated GA4 stream, consent required, no cart interception. */
(() => {
  'use strict';
  if (window.SolsticeInternalTraffic?.active) return;
  if (window.SolsticeConversionTracker) return;
  const config = window.SolsticeConversionConfig || {};
  if (!/^G-[A-Z0-9]+$/.test(config.measurementId || '')) return;
  const allowedHosts = ['solsticebijoux.com', 'www.solsticebijoux.com', '0ai7i3-i7.myshopify.com', 'solsticebijoux.fr', 'www.solsticebijoux.fr'];
  if (!allowedHosts.includes(location.hostname) && !config.debug) return;
  if (config.designMode) return;
  const nativePageViews = config.nativePageViews === true;
  const storefrontRoots = ['', '/fr', '/fr-ch', '/en-gb', '/en-us', '/de-de', '/de-at', '/es-es', '/es-mx', '/es-cl', '/fi-fi', '/hu-hu', '/it-it', '/nl-nl', '/pl-pl', '/sv-se'];
  const root = String(config.storefrontRoot || '').replace(/\/$/, '');
  const localizedRoot = storefrontRoots.includes(root) ? root : '';
  const siteLanguage = /^[a-z]{2}$/.test(config.siteLanguage || '') ? config.siteLanguage : null;
  const VIRTUAL_PATH = (siteLanguage ? (localizedRoot || '/en') : '') + '/__configurateur';
  const shopPath = path => {
    const prefix = storefrontRoots.find(p => p && (path === p || path.startsWith(p + '/')));
    return prefix ? path.slice(prefix.length) || '/' : path;
  };
  let transportReady = false;
  let virtualOpen = false;
  let sourcePath = location.pathname;
  let permitted = false;
  let pageViewSent = false;
  const pending = [];
  let retryTimer = null;
  const consent = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  const cleanLocation = path => location.origin + path; // Drop query strings and fragments.

  function prepareTransport() {
    if (transportReady || !consent()) return;
    if (nativePageViews) {
      // The Google channel runs in this page and owns loading/configuration.
      // A gtag function alone can belong to Ads or an unconfigured queue.
      transportReady = typeof window.gtag === 'function' &&
        Array.isArray(window.dataLayer) && window.dataLayer.some(command =>
          command?.[0] === 'config' && command[1] === config.measurementId);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    // This store's native privacy choice is authoritative for analytics storage.
    window.gtag('consent', 'update', { analytics_storage: 'granted' });
    const existingTag = Array.from(document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]')).find(script => {
      try { return new URL(script.src, location.href).searchParams.get('id') === config.measurementId; }
      catch { return false; }
    });
    if (existingTag) {
      if (!document.head.contains(existingTag)) document.head.appendChild(existingTag);
    } else {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.measurementId);
      document.head.appendChild(script);
      window.gtag('js', new Date());
    }
    // The native Google channel owns standard page views and purchases.
    window.gtag('config', config.measurementId, {
      send_page_view: false,
      ...(nativePageViews ? {} : { cookie_prefix: 'solstice_shopify' }),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    transportReady = true;
  }

  function clearPending() {
    pending.length = 0;
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
  }

  function flushPending() {
    retryTimer = null;
    if (!consent()) { clearPending(); return; }
    prepareTransport();
    if (transportReady) {
      while (pending.length && consent()) window.gtag(...pending.shift());
    }
    if (pending.length && retryTimer === null) retryTimer = setTimeout(flushPending, 100);
  }

  function send(name, path = location.pathname, params = {}) {
    if (!consent()) return false;
    try {window.SolsticeClarity?.event?.(name);} catch (_) {}
    prepareTransport();
    const command = ['event', name, {
      send_to: config.measurementId,
      page_location: cleanLocation(path),
      page_title: path === VIRTUAL_PATH ? 'Configurateur Solstice' : document.title,
      transport_type: 'beacon',
      ...(siteLanguage ? {site_language: siteLanguage} : {}),
      ...(config.marketCountry ? {storefront_country: config.marketCountry} : {}),
      ...(config.debug ? {debug_mode:true} : {}),
      ...params,
    }];
    if (!transportReady) {
      // Memory only, after consent, bounded to this page. Never bootstrap a
      // second Google tag to compensate for a late or unavailable native tag.
      if (pending.length >= 64) return false;
      pending.push(command);
      if (retryTimer === null) retryTimer = setTimeout(flushPending, 100);
    } else {
      if (pending.length) {
        if (retryTimer !== null) clearTimeout(retryTimer);
        flushPending();
      }
      window.gtag(...command);
    }
    return true;
  }

  function isConfiguratorVisible() {
    const wrapper = document.getElementById('ear-configurator-wrapper');
    return Boolean(window.isEarAppMounted && document.body?.classList.contains('ear-app-is-open') &&
      !document.documentElement.classList.contains('ear-force-loading-app') && wrapper &&
      !wrapper.closest('[v-cloak]') && wrapper.getClientRects().length && getComputedStyle(wrapper).visibility !== 'hidden');
  }

  function updateVisibility() {
    const visible = isConfiguratorVisible();
    if (!visible) { virtualOpen = false; return; }
    if (virtualOpen || !consent()) return;
    sourcePath = location.pathname;
    // Opening belongs to the source URL. The modal has its own page-view denominator.
    if (send('solstice_configurator_open', sourcePath)) {
      send('page_view', VIRTUAL_PATH, { page_referrer: cleanLocation(sourcePath) });
      virtualOpen = true;
      if (window.SolsticeComposerHasSelection) {try {window.SolsticeClarity?.event?.('solstice_configurator_selected');} catch (_) {}}
    }
  }

  function syncConsent() {
    const next = consent();
    if (!next) {
      // No event is replayed after consent revocation or a later grant.
      clearPending();
      if (permitted && !nativePageViews) window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
      virtualOpen = false;
    }
    if (!nativePageViews && next && !permitted && transportReady) {
      window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    }
    permitted = next;
    if (next) {
      prepareTransport();
      if (!nativePageViews && !pageViewSent) {
        const attribution = {};
        const query = new URL(location.href).searchParams;
        for (const [key, field] of Object.entries({utm_source:'campaign_source',utm_medium:'campaign_medium',utm_campaign:'campaign_name',utm_id:'campaign_id',utm_term:'campaign_term',utm_content:'campaign_content'})) {
          if (query.has(key)) attribution[field] = query.get(key).slice(0,100);
        }
        pageViewSent = send('page_view', location.pathname, attribution);
      }
      updateVisibility();
    }
  }

  function trackClick(event) {
    if (event.defaultPrevented || (event.button != null && event.button !== 0 && event.button !== 1) || !consent()) return;
    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || link.getAttribute('aria-disabled') === 'true') return;
    let destination;
    try { destination = new URL(link.href, location.href); } catch { return; }
    if (destination.origin !== location.origin || !/^https?:$/.test(destination.protocol)) return;
    if (destination.pathname === location.pathname && destination.search === location.search) return;
    const current = config.pageType;
    const route = shopPath(destination.pathname);
    const isProduct = /^\/(?:collections\/[^/]+\/)?products\/[^/]+\/?$/.test(route);
    const isCollection = /^\/collections\/[^/]+\/?$/.test(route);
    const isHome = route === '/';
    if (current === 'collection') {
      const page = Number(destination.searchParams.get('page'));
      const currentPage = Number(new URL(location.href).searchParams.get('page') || 1);
      if (destination.pathname === location.pathname && Number.isInteger(page) && page > currentPage) {
        send('solstice_collection_next');
      } else if (isProduct) {
        send('solstice_collection_product');
      }
    }
    if (current === 'article' && (isProduct || isCollection || isHome)) send('solstice_blog_to_shop');
    if (current === 'index' && (isProduct || isCollection)) send('solstice_shop_navigation');
  }

  document.addEventListener('click', trackClick);
  document.addEventListener('auxclick', trackClick);
  document.addEventListener('visitorConsentCollected', syncConsent);
  window.addEventListener('solstice:configurator:mounted', updateVisibility);
  window.addEventListener('solstice:conversion:cart-added', () => {
    send('solstice_add_to_cart');
  });
  window.addEventListener('solstice:conversion:configuration-added', () => {
    if (!consent() || !isConfiguratorVisible()) return;
    updateVisibility();
    if (virtualOpen) send('solstice_config_add_to_cart', VIRTUAL_PATH);
  });
  const observer = new MutationObserver(updateVisibility);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  if (document.body) {
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      syncConsent();
    }, {once:true});
  }
  // A render can finish after the class mutation; the mounted event also checks visibility.
  window.addEventListener('pageshow', syncConsent);
  window.addEventListener('pagehide', () => {
    if (retryTimer !== null) clearTimeout(retryTimer);
    flushPending();
    clearPending();
    if (nativePageViews) virtualOpen = false;
  });
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => { if (!error) syncConsent(); });
  }
  window.SolsticeConversionTracker = Object.freeze({ version: '2.4.0', measurementId: config.measurementId, check: updateVisibility });
  syncConsent();
})();

/* BEGIN local measurement diagnostic */
/* Explicit, local-only measurement check. Never changes tracking choices. */
(() => {
 'use strict';
 if(typeof location==='undefined'||typeof document==='undefined'||!/(?:^\?|&)_solstice_diagnostic=1(?:&|$)/.test(location.search||''))return;
 if(!['solsticebijoux.com','www.solsticebijoux.com','0ai7i3-i7.myshopify.com'].includes(location.hostname)||window.SolsticeConversionConfig?.designMode)return;
 try{const p=new URL(location.href).searchParams;if(p.getAll('_solstice_diagnostic').length!==1||p.get('_solstice_diagnostic')!=='1')return;}catch{return;}
 if(window.SolsticeMeasurementDiagnostic)return;
 const id='G-76YRM6TFQQ';let panel,timer,closed=false,attempts=0;
 const boolean=v=>typeof v==='boolean'?v:null;
 function read(){
  let internalCookie=null,legacyPilot=null,savedInternal=null;
  try{
   const cookies=(document.cookie||'').split(';').map(v=>v.trim());
   const value=name=>cookies.find(v=>v.startsWith(name+'='))?.slice(name.length+1);
   internalCookie=value('solstice_internal_v1')==='1';
   const previous=value('solstice_attribution_v1');
   legacyPilot=previous?JSON.parse(decodeURIComponent(previous))?.pilot==='c2-20261027':false;
  }catch{}
  try{savedInternal=localStorage.getItem('solstice_internal_v1')==='1';}catch{}
  const privacy=window.Shopify?.customerPrivacy;
  let analytics=null,marketing=null;
  try{analytics=boolean(privacy?.analyticsProcessingAllowed?.());}catch{}
  try{marketing=boolean(privacy?.marketingAllowed?.());}catch{}
  let configured=false;
  try{configured=Array.isArray(window.dataLayer)&&window.dataLayer.some(c=>c?.[0]==='config'&&c[1]===id);}catch{}
  return Object.freeze({version:1,internal: boolean(window.SolsticeInternalTraffic?.active),gaDisabled:window['ga-disable-'+id]===true,internalCookie,legacyPilot,savedInternal,analytics,marketing,ga4Configured:configured,googleSdkPresent:!!window.google_tag_manager?.[id]});
 }
 const yes=v=>v===true?'Oui':v===false?'Non':'Non vérifié';
 function render(){
  if(closed||!panel)return;
  const state=read(),list=panel.querySelector('dl');
  list.replaceChildren();
  for(const [label,value] of [
   ['Exclusion interne active',yes(state.internal)],['Blocage GA4 par le code',yes(state.gaDisabled)],
   ['Ancien marqueur de test',yes(state.legacyPilot)],['Accord Analyses',yes(state.analytics)],
   ['Accord Publicitaires',yes(state.marketing)],['Configuration GA4 présente',yes(state.ga4Configured)],['SDK Google présent',yes(state.googleSdkPresent)],['Réception chez Google','Non testée ici']
  ]){const term=document.createElement('dt'),detail=document.createElement('dd');term.textContent=label;detail.textContent=value;detail.style.cssText='margin:0 0 10px;font-weight:700';list.append(term,detail);}
  panel.querySelector('[data-diagnostic-result]').textContent=state.gaDisabled===true?'Ce navigateur bloque volontairement les envois GA4.':state.analytics===false&&state.marketing===false?'Les autorisations nécessaires au démarrage du pixel Google ne sont pas accordées.':'Ce contrôle ne prouve pas la réception d’un achat par Google.';
 }
 function close(){closed=true;if(timer!==undefined)clearTimeout(timer);document.removeEventListener('visitorConsentCollected',render);panel?.remove();}
 function mount(){
  if(closed)return;
  panel=document.createElement('aside');panel.id='solstice-measurement-diagnostic';panel.setAttribute('role','region');panel.setAttribute('aria-label','Diagnostic du suivi');
  panel.style.cssText='position:fixed;right:12px;bottom:12px;left:12px;max-width:430px;margin-left:auto;max-height:75vh;overflow:auto;z-index:2147483645;background:#fff;color:#172d24;border:2px solid #172d24;border-radius:12px;padding:18px;font:14px/1.4 system-ui;box-shadow:0 4px 24px #0003;text-align:left';
  const title=document.createElement('h2');title.textContent='Diagnostic du suivi';title.style.cssText='font:700 20px/1.2 system-ui;margin:0 0 12px';
  const result=document.createElement('p');result.setAttribute('data-diagnostic-result','');
  const list=document.createElement('dl');
  const note=document.createElement('p');note.textContent='Lecture seule, sur cette page de la boutique. Ce contrôle ne décrit pas la page de paiement, ne change pas vos choix, ne transmet aucun résultat et ne nécessite aucun achat.';
  const refresh=document.createElement('button');refresh.type='button';refresh.textContent='Actualiser le diagnostic';refresh.addEventListener('click',render);
  const exit=document.createElement('button');exit.type='button';exit.textContent='Fermer le diagnostic';exit.addEventListener('click',close);
  for(const b of [refresh,exit])b.style.cssText='display:inline-block;margin:4px 8px 0 0;padding:9px 12px;border:1px solid #172d24;border-radius:6px;background:#fff;color:#172d24;cursor:pointer;font:600 13px system-ui';
  panel.append(title,result,list,note,refresh,exit);document.body.appendChild(panel);render();
  document.addEventListener('visitorConsentCollected',render);
  const retry=()=>{if(closed)return;render();if(++attempts<25)timer=setTimeout(retry,200);};timer=setTimeout(retry,200);
 }
 window.SolsticeMeasurementDiagnostic=Object.freeze({version:1,read,close});
 if(document.body)mount();else document.addEventListener('DOMContentLoaded',mount,{once:true});
})();

/* END local measurement diagnostic */

/* BEGIN Conversion Lab Google identity, built from solstice-ga4-context.js */
/* Read the existing native Google identity only; never configure or send GA4. */
(() => {
 'use strict';
 if(window.SolsticeGA4Context)return;
 const id=window.SolsticeConversionConfig?.measurementId;
 if(id!=='G-76YRM6TFQQ'||window.SolsticeConversionConfig?.designMode)return;
 let generation=0,timer=null,last=0,busy=false;
 const permitted=()=>{
  try{return !window.SolsticeInternalTraffic?.active&&!window['ga-disable-'+id]&&
   window.Shopify?.customerPrivacy?.analyticsProcessingAllowed()===true&&
   window.Shopify?.customerPrivacy?.marketingAllowed()===true;}
  catch{return false;}
 };
 const active=epoch=>epoch===generation&&permitted();
 function publish(epoch,client,session){
  if(!active(epoch)||typeof window.Shopify?.analytics?.publish!=='function'||!window.crypto?.randomUUID)return;
  const available=typeof client==='string'&&/^[0-9]{1,20}\.[0-9]{1,20}$/.test(client)&&/^[0-9]{10}$/.test(String(session||''));
  const value={version:1,id:window.crypto.randomUUID(),measurement_id:id,observed_at:new Date().toISOString(),
   status:available?'available':'unavailable',client_id:available?client:null,session_id:available?String(session):null};
  // Promise errors from a temporarily unavailable Shopify event bus must not
  // affect navigation, cart, or checkout.
  try{Promise.resolve(window.Shopify.analytics.publish('solstice_ga4_context',value)).catch(()=>{});}catch{}
 }
 function capture(force=false){
  if(!permitted()||busy||(!force&&Date.now()-last<60000))return;
  const epoch=generation;last=Date.now();
  const configured=typeof window.gtag==='function'&&Array.isArray(window.dataLayer)&&
   window.dataLayer.some(command=>command?.[0]==='config'&&command[1]===id);
  if(!configured){publish(epoch,null,null);return;}
  busy=true;let client,session,finished=false;
  const finish=()=>{if(finished)return;finished=true;clearTimeout(timeout);busy=false;publish(epoch,client,session);};
  const timeout=setTimeout(finish,2000);
  try{
   window.gtag('get',id,'client_id',value=>{client=value;if(session!==undefined)finish();});
   window.gtag('get',id,'session_id',value=>{session=value;if(client!==undefined)finish();});
  }catch{finish();}
 }
 function sync(){
  generation++;busy=false;if(timer!==null)clearTimeout(timer);timer=null;
  if(!permitted())return;
  capture(true);
  // Allow the native loader and the consent-bound app pixel to finish startup.
  timer=setTimeout(()=>{timer=null;capture(true);},5000);
 }
 document.addEventListener('visitorConsentCollected',sync);
 document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='visible')capture();});
 document.addEventListener('pointerdown',()=>capture(),{passive:true});
 window.addEventListener('pageshow',sync);
 window.addEventListener('pagehide',()=>{generation++;if(timer!==null)clearTimeout(timer);timer=null;busy=false;});
 window.SolsticeGA4Context=Object.freeze({version:1});
 sync();
})();

/* BEGIN Conversion Lab session lifecycle, built from solstice-session-context.js */
/* Minimal page lifecycle, no pre-consent queue, identity or private URL. */
(() => {
 'use strict';
 if(typeof location==='undefined'||typeof document==='undefined')return;
 if(!['solsticebijoux.com','www.solsticebijoux.com','0ai7i3-i7.myshopify.com'].includes(location.hostname)||window.SolsticeConversionConfig?.designMode)return;
 if(window.__solsticeSessionContext)return;window.__solsticeSessionContext=true;
 const id=globalThis.crypto?.randomUUID?.();if(!id)return;
 let seq=0;
 const allowed=()=>{try{return Shopify.customerPrivacy.analyticsProcessingAllowed()===true&&Shopify.customerPrivacy.marketingAllowed()===true;}catch{return false;}};
 function publish(kind,extra={}){
  if(!allowed()||typeof window.Shopify?.analytics?.publish!=='function')return;
  const surface=window.matchMedia?.('(display-mode: standalone)').matches?'pwa':'web';
  // Exact allowlist before the shared Shopify bus. No href, referrer, contact,
  // marketing ID, checkout token, stored visitor ID or native transfer token.
  const data={version:1,page_instance_id:id,seq:seq++,surface};
  if(typeof extra.persisted==='boolean')data.persisted=extra.persisted;
  if(typeof extra.destination_host==='string'&&/^[a-z0-9.-]{1,253}$/.test(extra.destination_host))data.destination_host=extra.destination_host;
  try{Shopify.analytics.publish('solstice_cl:'+kind,data);}catch{}
 }
 document.addEventListener('visibilitychange',()=>publish(document.visibilityState==='hidden'?'hidden':'visible'));
 window.addEventListener('pagehide',event=>publish('pagehide',{persisted:event.persisted}));
 window.addEventListener('pageshow',event=>publish('pageshow',{persisted:event.persisted}));
 document.addEventListener('click',event=>{
  const link=event.target?.closest?.('a[href]');if(!link)return;
  try{const u=new URL(link.href);if(u.protocol==='https:'&&u.hostname!==location.hostname)publish('outbound',{destination_host:u.hostname});}catch{}
 },{capture:true,passive:true});
 // No heartbeat by default: passive tabs cannot manufacture engagement.
})();
